import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, FileText, Image as ImageIcon, Trash2, ExternalLink } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const MAX = 15 * 1024 * 1024;
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

// Which vehicle document this field manages (same chunked upload flow, different endpoints/labels).
const KINDS = {
  estimate: {
    field: "repair_estimate_file", attach: (id) => `/vehicles/${id}/as-is/file`, view: (id) => `/vehicles/${id}/estimate`,
    title: "Shop estimate document", hint: "PDF or photo from your service department / Rapid Recon. Shoppers open exactly this file.",
    cta: "Upload estimate (PDF or photo)", done: "Estimate document attached", removed: "Estimate document removed",
    icon: "text-amber-400", btn: "border-amber-500/50 bg-amber-950/20 hover:bg-amber-950/40 text-amber-300", bar: "bg-amber-400", testId: "estimate-file",
  },
  recon: {
    field: "recon_file", attach: (id) => `/vehicles/${id}/insider/recon/file`, view: (id) => `/vehicles/${id}/recon`,
    title: "Reconditioning report", hint: "The completed recon / inspection report (PDF or photo). Shoppers who unlock the wall open exactly this file.",
    cta: "Upload recon report (PDF or photo)", done: "Recon report attached", removed: "Recon report removed",
    icon: "text-[#7FC3FF]", btn: "border-[#0A8CF2]/50 bg-[#0A8CF2]/10 hover:bg-[#0A8CF2]/20 text-[#7FC3FF]", bar: "bg-[#0A8CF2]", testId: "recon-file",
  },
};

// Upload a vehicle document in chunks so big phone photos clear proxy limits.
export function EstimateFileField({ vehicle, onChanged, disabled, kind = "estimate" }) {
  const cfg = KINDS[kind] || KINDS.estimate;
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);   // null | 0..100
  const [removing, setRemoving] = useState(false);
  const file = vehicle?.[cfg.field];
  const t = cfg.testId;

  const pick = () => inputRef.current?.click();

  const upload = async (f) => {
    if (!f) return;
    if (!ACCEPT.split(",").includes(f.type)) return toast.error("Upload a PDF, JPG, PNG or WEBP");
    if (f.size > MAX) return toast.error("Keep the document under 15 MB");
    setProgress(0);
    try {
      const { data: start } = await api.post("/uploads/start");
      const total = Math.ceil(f.size / start.chunk_size);
      for (let i = 0; i < total; i++) {
        const blob = f.slice(i * start.chunk_size, (i + 1) * start.chunk_size);
        await api.put(`/uploads/${start.upload_id}/chunk/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      const { data } = await api.post(cfg.attach(vehicle.id), { upload_id: start.upload_id, filename: f.name, content_type: f.type, total_chunks: total });
      setProgress(100);
      onChanged?.(data);
      toast.success(cfg.done);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed — try again");
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      const { data } = await api.delete(cfg.attach(vehicle.id));
      onChanged?.(data);
      toast.success(cfg.removed);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div data-testid={`${t}-field`} className="rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid={`${t}-input`} onChange={(e) => upload(e.target.files?.[0])} />
      <div className="text-sm font-medium">{cfg.title}</div>
      <div className="text-[11px] text-slate-500 mb-3">{cfg.hint}</div>
      {file ? (
        <div className="flex items-center gap-3" data-testid={`${t}-current`}>
          {file.content_type === "application/pdf" ? <FileText className={`h-8 w-8 ${cfg.icon} shrink-0`} /> : <ImageIcon className={`h-8 w-8 ${cfg.icon} shrink-0`} />}
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate" data-testid={`${t}-name`}>{file.filename}</div>
            <div className="text-[11px] text-slate-500">{fmtSize(file.size)} · uploaded {new Date(file.uploaded_at).toLocaleDateString()}</div>
          </div>
          <Button asChild variant="outline" size="sm" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200 h-8">
            <a href={`${API}${cfg.view(vehicle.id)}`} target="_blank" rel="noopener" data-testid={`${t}-view`}><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> View</a>
          </Button>
          <Button variant="outline" size="sm" onClick={pick} disabled={disabled || progress != null} data-testid={`${t}-replace`} className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200 h-8">Replace</Button>
          <button type="button" onClick={remove} disabled={disabled || removing} data-testid={`${t}-remove`} title="Remove document" className="h-8 w-8 rounded-md border border-slate-800 text-slate-500 hover:text-red-400 hover:border-red-900 flex items-center justify-center">
            {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      ) : (
        <Button type="button" variant="outline" onClick={pick} disabled={disabled || progress != null} data-testid={`${t}-upload`} className={`w-full h-10 border-dashed ${cfg.btn}`}>
          {progress != null ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {progress != null ? `Uploading… ${progress}%` : cfg.cta}
        </Button>
      )}
      {progress != null && (
        <div className="mt-2 h-1.5 rounded bg-slate-800 overflow-hidden" data-testid={`${t}-upload-progress`}>
          <div className={`h-full ${cfg.bar} transition-[width]`} style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}
